import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DeliveryTrackerResultUploadDialogComponent } from '@features/organization/components/delivery-tracker-detail/components/delivery-tracker-result-upload-dialog/delivery-tracker-result-upload-dialog.component';
import { getDeliveryTrackerDocumentColumnsConfig } from '@features/organization/components/delivery-tracker-detail/configuration/delivery-tracker-document-table.config';
import { DeliveryTrackerDocumentFacade } from '@features/organization/components/delivery-tracker-detail/facades/delivery-tracker-document.facade';
import type {
  DeliveryTrackerDocument,
  DeliveryTrackerResultDelivery,
  DeliveryTrackerResultUploadDialogData,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import { TranslocoDirective } from '@jsverse/transloco';
import type { ResponseDeliveryTrackerCacheDto } from '@kerberos-compliance/compliance-shared-ts/features/delivery/dtos/response.delivery-tracker.cache-dto';
import {
  AppDatatableComponent,
  ButtonComponent,
  ButtonVariantMap,
  IconNameMap,
  ModalService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';

@Component({
  selector: 'aad-delivery-tracker-documents',
  imports: [AppDatatableComponent, ButtonComponent, TranslocoDirective],
  templateUrl: 'delivery-tracker-documents.component.html',
  styleUrl: 'delivery-tracker-documents.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackerDocumentsComponent implements OnInit {
  public readonly deliveryTracker = input.required<ResponseDeliveryTrackerCacheDto>();

  protected readonly ButtonVariantMap = ButtonVariantMap;

  protected readonly IconNameMap = IconNameMap;

  private readonly documentFacade = inject(DeliveryTrackerDocumentFacade);

  private readonly modalService = inject(ModalService);

  private readonly translationService = inject(TranslationService);

  protected readonly tableColumns = getDeliveryTrackerDocumentColumnsConfig(
    this.translationService,
    (document) => this.documentFacade.previewDocument(document),
    {
      download: (document) => void this.download(document),
      delete: (document) => void this.documentFacade.deleteDocument(this.deliveryTracker(), document),
      deleteState: (document) => this.documentFacade.deleteStateFor(this.deliveryTracker(), document),
      isDeleting: (document) => this.documentFacade.deletingDocumentId() === document.id,
    },
  );

  protected readonly documents = computed(() => this.documentFacade.documentsForTracker(this.deliveryTracker().id));

  protected readonly isLoading = computed(() => this.documentFacade.isLoadingForTracker(this.deliveryTracker().id));

  protected readonly isDelivering = computed(
    () => this.documentFacade.deliveringTrackerId() === this.deliveryTracker().id,
  );

  public ngOnInit(): void {
    void this.documentFacade.loadDocuments(this.deliveryTracker().id);
  }

  /**
   * Opens the result-delivery dialog for this tracker. The dialog submits the delivery itself and stays open
   * until it went through, so a slow or failed upload is never hidden behind an already closed dialog. Whether
   * the delivery also closes the tracker is decided in the dialog.
   *
   * @returns {void} This method does not return a value.
   */
  protected openResultUploadDialog(): void {
    const tracker = this.deliveryTracker();
    this.modalService.showComponentInModal<
      DeliveryTrackerResultUploadDialogComponent,
      DeliveryTrackerResultUploadDialogData
    >(DeliveryTrackerResultUploadDialogComponent, {
      content: '',
      data: () => ({
        tracker,
        deliver: (delivery: DeliveryTrackerResultDelivery) => this.documentFacade.deliverResult(tracker, delivery),
      }),
    });
  }

  /**
   * Downloads one document of this tracker.
   *
   * @param {DeliveryTrackerDocument} document - The row that was clicked.
   * @returns {Promise<void>} Resolves once the download was handed to the browser.
   */
  private async download(document: DeliveryTrackerDocument): Promise<void> {
    await this.documentFacade.downloadDocument(document);
  }
}
